// src/features/analytics/useAnalyticsMediaRetention.ts

import { useQuery } from '@tanstack/react-query';
import { AnalyticsApi } from './AnalyticsApi';
import { ANALYTICS_QUERY_KEYS } from '@/lib/constants/analytics';
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags';
import type { UtcRange } from '@/shared/utils/utcRange';
import type { AnalyticsEnvelope, MediaRetentionData } from './types';

export interface UseAnalyticsMediaRetentionResult {
  retention: AnalyticsEnvelope<MediaRetentionData> | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Per-media audience retention for the Content-tab Drawer (metrics expansion)
 * — account scope only, VOD only (the Drawer passes null mediaId for live
 * rows), ranged one-shot (never polled). The build-time expansion flag
 * hard-disables the query while the feature ships dark.
 */
export function useAnalyticsMediaRetention(
  cid: number | null,
  mediaId: string | null,
  range: UtcRange
): UseAnalyticsMediaRetentionResult {
  const query = useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.mediaRetention(cid ?? 0, mediaId ?? '', range.from, range.to),
    queryFn: () => AnalyticsApi.getMediaRetention(cid!, mediaId!, range),
    enabled: FEATURE_FLAGS.analyticsExpansionEnabled && cid !== null && mediaId !== null,
    staleTime: 60_000,
  });

  return {
    retention: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
